import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronRight, ChevronDown, LogOut, Layers } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { signOut } from '../lib/supabase';
import './Navbar.css';

const NAV_LINKS = [
  { label: 'Features', to: '/#features' },
  { label: 'Tools',    to: '/tools' },
  { label: 'Pricing',  to: '/pricing' },
  { label: 'Blog',     to: '/blog' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, displayName, initial } = useAuth();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleAnchor = (e, to) => {
    if (to.startsWith('/#')) {
      e.preventDefault();
      const id = to.slice(2);
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleSignOut = async () => {
    setDropdownOpen(false);
    await signOut();
    navigate('/');
  };

  return (
    <>
      <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="navbar__inner">
          {/* Logo */}
          <Link to="/" className="navbar__logo">
            <div className="navbar__logo-icon">
              <img src="/brain-64.png" alt="ToolMind" className="navbar__logo-img" />
            </div>
            <span className="navbar__logo-text">ToolMind</span>
            <span className="navbar__logo-badge">AI</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="navbar__links">
            {NAV_LINKS.map(({ label, to }) => (
              <Link
                key={label}
                to={to}
                onClick={(e) => handleAnchor(e, to)}
                className={`navbar__link ${location.pathname === to ? 'navbar__link--active' : ''}`}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="navbar__actions">
            {user ? (
              /* Logged-in state */
              <div className="navbar__user" ref={dropdownRef}>
                <button
                  className="navbar__user-btn"
                  onClick={() => setDropdownOpen(v => !v)}
                  aria-expanded={dropdownOpen}
                >
                  <div className="navbar__avatar">{initial}</div>
                  <span className="navbar__user-name">{displayName}</span>
                  <ChevronDown size={14} className={`navbar__chevron ${dropdownOpen ? 'open' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="navbar__dropdown glass-hi">
                    <div className="navbar__dropdown-header">
                      <div className="navbar__avatar navbar__avatar--lg">{initial}</div>
                      <div>
                        <div className="navbar__dropdown-name">{displayName}</div>
                        <div className="navbar__dropdown-email">{user.email}</div>
                      </div>
                    </div>
                    <div className="navbar__dropdown-divider" />
                    <Link to="/stack" className="navbar__dropdown-item" onClick={() => setDropdownOpen(false)}>
                      <Layers size={14} />
                      My Stack
                    </Link>
                    <div className="navbar__dropdown-divider" />
                    <button className="navbar__dropdown-item" onClick={handleSignOut}>
                      <LogOut size={14} />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Logged-out state */
              <>
                <Link to="/signup" className="btn btn-ghost btn-sm">Sign in</Link>
                <Link to="/signup" className="btn btn-primary btn-sm">
                  Get Started <ChevronRight size={14} />
                </Link>
              </>
            )}
          </div>

          {/* Hamburger */}
          <button
            className="navbar__hamburger"
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileOpen ? 'mobile-menu--open' : ''}`}>
        <nav className="mobile-menu__links">
          {NAV_LINKS.map(({ label, to }) => (
            <Link
              key={label}
              to={to}
              onClick={(e) => handleAnchor(e, to)}
              className="mobile-menu__link"
            >
              {label}
            </Link>
          ))}
        </nav>
        <div className="mobile-menu__actions">
          {user ? (
            <>
              <div className="mobile-menu__user">
                <div className="navbar__avatar">{initial}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{displayName}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{user.email}</div>
                </div>
              </div>
              <Link to="/stack" className="btn btn-ghost" style={{ justifyContent: 'center' }} onClick={() => setMobileOpen(false)}>
                <Layers size={15} /> My Stack
              </Link>
              <button
                className="btn btn-ghost"
                style={{ justifyContent: 'center' }}
                onClick={handleSignOut}
              >
                <LogOut size={15} /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/signup" className="btn btn-ghost" style={{ justifyContent: 'center' }}>Sign in</Link>
              <Link to="/signup" className="btn btn-primary" style={{ justifyContent: 'center' }}>
                Get Started <ChevronRight size={16} />
              </Link>
            </>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div className="mobile-menu__overlay" onClick={() => setMobileOpen(false)} />
      )}
    </>
  );
}
